import { useState, useEffect } from 'react'
import { Settings, Save, Eye, EyeOff, Clock } from 'lucide-react'

interface PlatformConfigItem {
  key: string
  value: unknown
  type: 'string' | 'number' | 'boolean' | 'json'
  description: string | null
  category: string
  is_sensitive: boolean
  updated_at: string
  updated_by: string | null
}

interface ConfigFormProps {
  configs: PlatformConfigItem[]
  loading?: boolean
  onUpdate: (key: string, value: unknown) => Promise<void>
  updating?: boolean
}

export function ConfigForm({ configs, loading = false, onUpdate, updating = false }: ConfigFormProps) {
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editValue, setEditValue] = useState<string>('')
  const [showSensitive, setShowSensitive] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (editingKey) {
      const config = configs.find((c) => c.key === editingKey)
      if (config) {
        if (config.type === 'json') {
          setEditValue(JSON.stringify(config.value, null, 2))
        } else {
          setEditValue(String(config.value))
        }
      }
    }
  }, [editingKey, configs])

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      general: 'Geral',
      billing: 'Cobrança',
      email: 'Email',
      security: 'Segurança',
      features: 'Funcionalidades',
      limits: 'Limites',
    }
    return labels[category] ?? category
  }

  const handleSave = async (config: PlatformConfigItem) => {
    let parsedValue: unknown = editValue

    if (config.type === 'number') {
      parsedValue = parseFloat(editValue)
      if (isNaN(parsedValue as number)) {
        alert('Valor numérico inválido')
        return
      }
    } else if (config.type === 'boolean') {
      parsedValue = editValue === 'true'
    } else if (config.type === 'json') {
      try {
        parsedValue = JSON.parse(editValue)
      } catch {
        alert('JSON inválido')
        return
      }
    }

    await onUpdate(config.key, parsedValue)
    setEditingKey(null)
  }

  const toggleSensitive = (key: string) => {
    const newSet = new Set(showSensitive)
    if (newSet.has(key)) {
      newSet.delete(key)
    } else {
      newSet.add(key)
    }
    setShowSensitive(newSet)
  }

  const renderValue = (config: PlatformConfigItem) => {
    if (config.is_sensitive && !showSensitive.has(config.key)) {
      return '••••••••'
    }

    if (config.type === 'boolean') {
      return config.value ? 'Sim' : 'Não'
    }

    if (config.type === 'json') {
      return JSON.stringify(config.value, null, 2)
    }

    return String(config.value)
  }

  const renderEditField = (config: PlatformConfigItem) => {
    if (config.type === 'boolean') {
      return (
        <select
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500"
        >
          <option value="true">Sim</option>
          <option value="false">Não</option>
        </select>
      )
    }

    if (config.type === 'json') {
      return (
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono text-sm focus:ring-2 focus:ring-teal-500 resize-none"
        />
      )
    }

    return (
      <input
        type={config.type === 'number' ? 'number' : 'text'}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500"
      />
    )
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-8 text-center">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-12 h-12 bg-gray-200 rounded-full" />
            <div className="h-4 bg-gray-200 rounded w-48" />
          </div>
        </div>
      </div>
    )
  }

  if (configs.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Settings className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-medium text-gray-900 mb-2">Nenhuma configuração encontrada</h3>
          <p className="text-sm text-gray-500">Esta categoria não possui configurações</p>
        </div>
      </div>
    )
  }

  // Group configs by category
  const groupedConfigs = configs.reduce<Record<string, PlatformConfigItem[]>>(
    (acc, config) => {
      const category = config.category
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category]!.push(config)
      return acc
    },
    {}
  )

  return (
    <div className="space-y-6">
      {Object.entries(groupedConfigs).map(([category, categoryConfigs]) => (
        <div key={category} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-medium text-gray-900">{getCategoryLabel(category)}</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {categoryConfigs.map((config) => (
              <div key={config.key} className="px-6 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900">{config.key}</p>
                      {config.is_sensitive && (
                        <button
                          onClick={() => toggleSensitive(config.key)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                        >
                          {showSensitive.has(config.key) ? (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      )}
                    </div>
                    {config.description && (
                      <p className="text-sm text-gray-500 mb-2">{config.description}</p>
                    )}
                    {editingKey === config.key ? (
                      <div className="space-y-3">
                        {renderEditField(config)}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSave(config)}
                            disabled={updating}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            {updating ? 'Salvando...' : 'Salvar'}
                          </button>
                          <button
                            onClick={() => setEditingKey(null)}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={() => setEditingKey(config.key)}
                        className="cursor-pointer group"
                      >
                        <div className="p-2 bg-gray-50 rounded-lg text-sm text-gray-700 font-mono group-hover:bg-gray-100 transition-colors">
                          {config.type === 'json' ? (
                            <pre className="whitespace-pre-wrap overflow-x-auto">
                              {renderValue(config)}
                            </pre>
                          ) : (
                            renderValue(config)
                          )}
                        </div>
                      </div>
                    )}
                    {config.updated_at && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-400">
                        <Clock className="w-3 h-3" />
                        Atualizado em {formatDateTime(config.updated_at)}
                        {config.updated_by && ` por ${config.updated_by}`}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                      {config.type}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
